import {
  Box,
  Combobox as ChakraCombobox,
  ComboboxRootProps,
  createListCollection,
  Field,
  InputGroup,
  Portal,
  Span,
  useFilter,
} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {LuSearch} from 'react-icons/lu'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {SelectItem} from './select.js'
import {buildSelectionLabel} from './selection-label.js'
import {useSelectField} from './use-select-field.js'

export interface ComboboxProps extends Omit<ComboboxRootProps, 'collection'> {
  items: SelectItem[]
  label: string
  hideLabel?: boolean
  /** Hides the leading magnifying glass on the control (the search still works). */
  hideSearchIcon?: boolean
  helpText?: string
  description?: string
  placeholder?: string
  autoComplete?: string
  controlProps?: ChakraCombobox.ControlProps
  contentProps?: ChakraCombobox.ContentProps
  inputProps?: ChakraCombobox.InputProps
  portalled?: boolean
  disabled?: boolean
  /** Shows a validation message under the field even when the form field has no error yet. */
  errorMessage?: string
  /** Opens the list as soon as the field mounts, e.g. when validation points at it. */
  autoOpen?: boolean
}

export function Combobox({required, hideSearchIcon, autoOpen, errorMessage, ...props}: ComboboxProps) {
  const {t} = useTranslate('common')
  const {contains} = useFilter({sensitivity: 'base'})
  const [filter, setFilter] = useState('')
  const [inputValue, setInputValue] = useState<string | undefined>(undefined)
  const [open, setOpen] = useState(Boolean(autoOpen))
  const {field, handleChange, value} = useSelectField()

  const {selectedText} = useMemo(() => {
    const selectedLabels = value
      .map(selected => props.items.find(item => item.value === selected)?.label)
      .filter((label): label is string => Boolean(label))
    const selectedText = buildSelectionLabel(selectedLabels[0], selectedLabels.length - 1, t('more'))
    return {selectedText}
  }, [props.items, value])

  // initialize the value
  useEffect(() => {
    setInputValue(prev => {
      if (prev !== undefined) return prev
      return selectedText
    })
  }, [selectedText])

  const collection = useMemo(() => {
    let filteredItems = props.items
    if (filter) {
      filteredItems = props.items.filter(item => contains(item.label, filter))
    }

    if (filteredItems.length === 0) {
      return createListCollection({
        items: [
          {
            label: t('no_options_available'),
            value: 'EMPTY_STATE',
          },
        ] as SelectItem[],
        isItemDisabled: item => item.value === 'EMPTY_STATE',
      })
    }

    return createListCollection({items: filteredItems})
  }, [props.items, contains, filter, t])

  const onValueChange = useCallback(
    (e: ChakraCombobox.ValueChangeDetails) => {
      const label = buildSelectionLabel(e.items?.[0]?.label, e.items?.length - 1, t('more'))
      setInputValue(label)

      // handle input clear (x button)
      if (e.value.length === 0) {
        setFilter('')
        handleChange(e.value)
        return
      }

      // guard against dummy item
      if (e.value[0] === 'EMPTY_STATE') return

      handleChange(e.value)
    },
    [field, handleChange]
  )

  const onInputValueChange: typeof props.onInputValueChange = changes => {
    if (changes.reason === 'item-select') return
    setInputValue(changes.inputValue)
    setFilter(changes.inputValue)
    props.onInputValueChange?.(changes)
  }

  const onOpenChange: typeof props.onOpenChange = details => {
    setOpen(details.open)

    if (details.open) {
      setInputValue('')
      setFilter('')
    } else {
      setInputValue(selectedText)
    }

    props.onOpenChange?.(details)
  }

  return (
    <FieldRoot required={required} disabled={props.disabled} invalid={Boolean(errorMessage)}>
      <ChakraCombobox.Root
        width="100%"
        collection={collection}
        name={field.name}
        value={value}
        disabled={props.disabled}
        open={open}
        openOnClick
        onValueChange={onValueChange}
        {...props}
        onInputValueChange={onInputValueChange}
        inputValue={inputValue}
        onOpenChange={onOpenChange}
        positioning={{sameWidth: true, ...props.positioning}}
      >
        <ChakraCombobox.Label srOnly={props.hideLabel} maxWidth="max-content">
          {props.label} {required && <FieldRequiredIndicator />}
        </ChakraCombobox.Label>

        <ChakraCombobox.Control backgroundColor="bg" {...props.controlProps}>
          <InputGroup startElement={hideSearchIcon ? undefined : <LuSearch />}>
            <ChakraCombobox.Input
              placeholder={props.placeholder}
              autoComplete={props.autoComplete}
              {...props.inputProps}
            />
          </InputGroup>
          <ChakraCombobox.IndicatorGroup>
            <ChakraCombobox.ClearTrigger />
            <ChakraCombobox.Trigger />
          </ChakraCombobox.IndicatorGroup>
        </ChakraCombobox.Control>

        <Portal disabled={!props.portalled}>
          <ChakraCombobox.Positioner width="100%">
            <ChakraCombobox.Content minWidth="20rem" {...props.contentProps}>
              {collection.items?.map(item => {
                const isEmptyState = item.value === 'EMPTY_STATE'

                return (
                  <ChakraCombobox.Item key={item.value} item={item}>
                    <Box display="flex" flexDirection="column">
                      <Span wordBreak="break-all" truncate>
                        {item.label}
                      </Span>
                      {!isEmptyState && item.description !== undefined && (
                        <Span fontSize="xs" color="fg.muted">
                          {item.description}
                        </Span>
                      )}
                    </Box>
                    {!isEmptyState && <ChakraCombobox.ItemIndicator />}
                  </ChakraCombobox.Item>
                )
              })}
            </ChakraCombobox.Content>
          </ChakraCombobox.Positioner>
        </Portal>
      </ChakraCombobox.Root>

      {props.helpText && <Field.HelperText textStyle="sm">{props.helpText}</Field.HelperText>}

      <ErrorMessage message={errorMessage} />
    </FieldRoot>
  )
}
